import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
} from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useShortcut } from 'utils/hooks/keyboard/useShortcut';
import { setPaymentSelected, setPayment } from 'actions/Payments';
import { getPaymentSelected, getPayments, getBalance } from 'reducers/Payments';
import {
  getCustomPaymentTypes,
  getCustomPaymentConfig,
} from 'reducers/PaymentTypes';
import { generateCustomPaymentKey } from 'utils/payments';
import { round } from 'utils';

function sanitizeAmount(raw: string) {
  let clean = raw
    .replace(/[,]/g, '.')
    .replace(/[^0-9.]/g, '')
    .replace(/[.](?=.*[.])/g, '');
  if (clean.match(/^[.]/g)) {
    clean = clean.replace(/^[.]/g, '0.');
  }
  if (clean.match(/^[0]+[1-9]/g)) {
    clean = clean.replace(/^[0]/g, '');
  }
  return clean;
}

function CustomPaymentType() {
  const { t } = useTranslation('payment');
  const dispatch = useDispatch();

  const balance = useSelector(getBalance);
  const payments = useSelector(getPayments);
  const paymentType = useSelector(getPaymentSelected).match(
    /custom-payment-type-(.+)/,
  )?.[1];
  const selectedPaymentType = useSelector(getCustomPaymentTypes).find(
    p => p.type === paymentType,
  );
  const customPaymentConfig = useSelector(getCustomPaymentConfig);

  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');

  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    inputRef?.current?.focus();
  }, []);

  const referenceNumberErrorText = !referenceNumber.trim()
    ? t('referenceNumberPrompt.valueIsEmpty')
    : '';

  const paymentAmountErrorText = (function getPaymentAmountErrorText() {
    const paymentAmountExceedsBalance =
      Number(paymentAmount) > Math.abs(Number(balance));

    if (!paymentAmount) {
      return t('referenceNumberPrompt.valueIsEmpty');
    }
    if (!Number(paymentAmount)) {
      return t('referenceNumberPrompt.amountIsZero');
    }
    if (paymentAmountExceedsBalance) {
      return t('referenceNumberPrompt.amountExceedsBalance', {
        balance: round(Math.abs(balance)),
      });
    }
    return '';
  })();

  const referenceNumberLabel = selectedPaymentType
    ? customPaymentConfig[selectedPaymentType.type]?.referenceNumberLabel
    : undefined;

  async function addPayment() {
    if (referenceNumberErrorText || paymentAmountErrorText) {
      return;
    }
    if (!selectedPaymentType) {
      return;
    }
    const keys = Object.keys(payments).filter(key =>
      key.includes(selectedPaymentType.type),
    );
    const paymentKey = generateCustomPaymentKey(keys, selectedPaymentType.type);
    await dispatch(
      setPayment({
        amount: round(paymentAmount),
        key: paymentKey,
        caption: selectedPaymentType?.name,
        type: selectedPaymentType?.type,
        paymentIntegration:
          selectedPaymentType?.type === 'CARD' ? 'external' : undefined,
        referenceNumber,
      }),
    );
    dispatch(setPaymentSelected(''));
  }

  function close() {
    dispatch(setPaymentSelected(''));
  }

  useShortcut('cancel', close, 30);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        addPayment();
      }}
    >
      <Typography variant="h6" paragraph>
        {selectedPaymentType?.name ?? paymentType}
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        margin="normal"
        type="text"
        label={
          referenceNumberLabel || t('referenceNumberPrompt.referenceNumber')
        }
        inputRef={inputRef}
        value={referenceNumber}
        onChange={e => setReferenceNumber(e.target.value)}
        error={!!referenceNumberErrorText}
        helperText={referenceNumberErrorText}
      />

      <TextField
        fullWidth
        variant="outlined"
        margin="normal"
        type="text"
        label={t('referenceNumberPrompt.paymentAmount')}
        value={paymentAmount}
        onChange={e => setPaymentAmount(sanitizeAmount(e.target.value))}
        error={!!paymentAmountErrorText}
        helperText={paymentAmountErrorText}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Button
                variant="contained"
                color="primary"
                disableElevation
                onClick={() =>
                  setPaymentAmount(sanitizeAmount(round(balance) ?? '0.00'))
                }
              >
                100%
              </Button>
            </InputAdornment>
          ),
        }}
      />
      <Box display="flex" gridGap="1rem" marginTop="1rem" justifyContent="end">
        <Button variant="outlined" disableElevation onClick={close}>
          {t('referenceNumberPrompt.cancel')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disableElevation
          type="submit"
          disabled={!!referenceNumberErrorText || !!paymentAmountErrorText}
        >
          {t('referenceNumberPrompt.confirm')}
        </Button>
      </Box>
    </form>
  );
}

export default CustomPaymentType;
